import { useEffect, useMemo, useState } from "react";
import { City, Town } from "../../models/IOrders";
import { getCityList, getCityWithTownList } from "../../services/locations";
import { PHONE_MAX_ADDRESS_DETAIL, PHONE_MAX_STREET } from "../models/IPhoneOrder";

export interface IPhoneAddressValue {
  cityId: string;
  townId: string;
  streetName: string;
  addressDetail: string;
}

interface Props {
  value: IPhoneAddressValue;
  onChange: (patch: Partial<IPhoneAddressValue>) => void;
  disabled?: boolean;
}

/** İlçe = City, bölge = Town. Bölge listesi ilçeye göre süzülür. */
export default function PhoneAddressFields({ value, onChange, disabled }: Props) {
  const [cities, setCities] = useState<City[] | null>(null);
  const [towns, setTowns] = useState<Town[] | null>(null);
  const [townsLoading, setTownsLoading] = useState(false);
  const [listError, setListError] = useState("");

  useEffect(() => {
    getCityList()
      .then((res) => setCities(res.data ?? []))
      .catch(() => setListError("İlçe listesi alınamadı."));
  }, []);

  useEffect(() => {
    if (!value.cityId) { setTowns(null); return; }
    setTownsLoading(true);
    getCityWithTownList(value.cityId)
      .then((res) => setTowns(res.data?.town ?? []))
      .catch(() => { setTowns([]); setListError("Bölge listesi alınamadı."); })
      .finally(() => setTownsLoading(false));
  }, [value.cityId]);

  // Listeler sunucudan sırasız geliyor; operatör aradığını gözle bulsun diye Türkçe alfabetik.
  const sortedCities = useMemo(
    () => [...(cities ?? [])].sort((a, b) => (a.cityNameTr ?? "").localeCompare(b.cityNameTr ?? "", "tr")),
    [cities]
  );
  const sortedTowns = useMemo(
    () => [...(towns ?? [])].sort((a, b) => (a.townNameTr ?? "").localeCompare(b.townNameTr ?? "", "tr")),
    [towns]
  );

  return (
    <>
      {listError && (
        <div className="dinein-alert dinein-alert--warn"><i className="bi bi-exclamation-triangle-fill"></i><span>{listError}</span></div>
      )}

      <div className="pc-form__row">
        <div className="pc-field">
          <label>İlçe <span className="pc-req">*</span></label>
          <select className="form-select" value={value.cityId} disabled={disabled || !cities}
            onChange={(e) => onChange({ cityId: e.target.value, townId: "" })}>
            <option value="">{cities ? "Seçiniz..." : "Yükleniyor..."}</option>
            {sortedCities.map((c) => <option key={c.id} value={c.id}>{c.cityNameTr}</option>)}
          </select>
        </div>
        <div className="pc-field">
          <label>Bölge <span className="pc-req">*</span></label>
          <select className="form-select" value={value.townId}
            disabled={disabled || !value.cityId || townsLoading}
            onChange={(e) => onChange({ townId: e.target.value })}>
            <option value="">
              {!value.cityId ? "Önce ilçe seçin" : townsLoading ? "Yükleniyor..." : "Seçiniz..."}
            </option>
            {sortedTowns.map((t) => <option key={t.id} value={t.id}>{t.townNameTr}</option>)}
          </select>
          {value.cityId && towns && towns.length === 0 && !townsLoading && (
            <div className="dinein-small dinein-muted">Bu ilçeye tanımlı bölge yok.</div>
          )}
        </div>
      </div>

      <div className="pc-field">
        <label>Sokak / Cadde <span className="pc-req">*</span></label>
        <input className="form-control" value={value.streetName} maxLength={PHONE_MAX_STREET} disabled={disabled}
          placeholder="Şehit Ecvet Yusuf Caddesi"
          onChange={(e) => onChange({ streetName: e.target.value })} />
      </div>

      <div className="pc-field">
        <label>Adres Detayı <span className="pc-req">*</span></label>
        <textarea className="form-control" rows={2} value={value.addressDetail} disabled={disabled}
          maxLength={PHONE_MAX_ADDRESS_DETAIL}
          placeholder="No 14, 3. kat, eczanenin üstü"
          onChange={(e) => onChange({ addressDetail: e.target.value })} />
        <div className="dinein-small dinein-muted">
          {value.addressDetail.length} / {PHONE_MAX_ADDRESS_DETAIL}
        </div>
      </div>
    </>
  );
}
